import { BREAKPOINTS } from "../config/breakpoints";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import DetailsComponent from "../Generic/DetailsPageComponents/DetailsComponent";
import { ContentComponent, ContentContainer } from "../Generic/DetailsPageComponents/ContentComponents";
import EmptyState from "../Generic/EmptyState";
import Button from "../Generic/Button";
import { isEmpty, setProperty } from "../util/util";
import ModalButton from "../Generic/ModalButton";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearState } from "./Actions/index";
import LandingConnector from "../Landing/Connector/LandingConnector";
import OrderService from "./Services/OrderService";
import { errorNotification } from "../Generic/Notifications";
import Loader from "../Generic/Loader";
import Timestamp from "../Generic/Timestamp";

const Page = styled.div`
	height: max-content;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 2rem;
	scroll-behavior: smooth;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		padding: 1rem;
	}
`;

const Pagelink = styled(Link).attrs({
	target: "_blank",
	rel: "noopener noreferrer", //Open in new tab
})`
	text-decoration: underline;
	cursor: pointer;
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: max-content;
	width: 100%;
	gap: 1rem;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-wrap: ${(props) => props.flexWrap};
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	min-width: 0;
	overflow-wrap: break-word;
	word-break: break-word;
	@media (max-width: ${BREAKPOINTS.tablet}px) {
		font-size: 1.5rem;
	}
`;

const DataTypes = {
	Object: "Object",
	List: "List",
};

const TaskTypes = {
	PREPARE: "prepare",
	PICK_PLATE: "pickPlate",
	SERVE: "serve",
	DISPOSE: "dispose",
};

function fancyTimeFormat(duration) {
	// Minutes and seconds
	const mins = Math.floor(duration / (60 * 1000));
	const secs = Math.floor((duration / 1000) % 60);

	const minutesSuffix = "m";
	const secondsSuffix = "s";

	var output = "";

	if (!mins && !secs) {
		return `0${secondsSuffix}`;
	}

	if (mins) {
		output += `${mins}${minutesSuffix} `;
	}

	if (secs) {
		output += `${secs}${secondsSuffix} `;
	}

	return output;
}


function parseConfig(config, lookupData) {
	if (!config) {
		return <EmptyState message="No config Provided" />;
	}

	let renderData = [];

	for (const group in config) {
		const { disableCollapse, initiallyExpanded, title, type, skipIfEmpty, children, retrieveListData, errored } = config[group];

		switch (type) {
			case DataTypes.List:
				let listGroupData = [],
					data;

				try {
					data = retrieveListData(lookupData);
				} catch (error) {
					console.log(`Could not render group ${group} due to the following issue in getting listdata : ${error.message}`);
				}

				if (isEmpty(data) && skipIfEmpty) continue;

				for (const dataIndex in data) {
					let rowData = [],
						rowLookup = data[dataIndex], 
						isErrored = false; //TODO: Add Component State Groups to handle this.

					if(errored) {
						try {
							const erroredValue = errored(rowLookup);
							if(erroredValue) {
								isErrored = true;
							}
						} catch (error) {
							console.log(`Could not render child with title ${title} of group ${group} due to error : ${error.message}`);
						}
					}

					for (const index in children) {
						const { title, value, render, omit: checkOmitCondition } = children[index];

						if (checkOmitCondition) {
							try {
								const omit = checkOmitCondition(rowLookup);
								if (omit) continue;
							} catch (error) {
								console.log(`Could not check omit condition for child with index ${index} of group ${group} due to error : ${error.message}`);
								continue;
							}
						}

						if (render) {
							try {
								rowData.push(render(rowLookup, lookupData, index, dataIndex));
							} catch (error) {
								console.log(`Could not render child with index ${index} of group ${group} due to error : ${error.message}`);
							}
						} else {
							let displayValue = "-";
							try {
								displayValue = value(rowLookup, lookupData, index, dataIndex);
							} catch (error) {
								console.log(`Could not render child with title ${title} of group ${group} due to error : ${error.message}`);
							}

							rowData.push(<ContentComponent key={`${group}-${index}`} title={title} value={displayValue} />);
						}
					}

					listGroupData.push(<ContentContainer isErrored={isErrored} key={`${group}-${dataIndex}`}>{rowData}</ContentContainer>);
				}

				renderData.push(
					<DetailsComponent
						isList={true}
						key={group}
						disableCollapse={disableCollapse}
						initiallyExpanded={initiallyExpanded}
						title={title}>
						{listGroupData}
					</DetailsComponent>
				);

				break;

			case DataTypes.Object:
			default:
				let groupData = [];

				for (const index in children) {
					const { title, value, render, omit: checkOmitCondition } = children[index];

					if (checkOmitCondition) {
						try {
							const omit = checkOmitCondition(lookupData);
							if (omit) continue;
						} catch (error) {
							console.log(`Could not check omit condition for child with index ${index} of group ${group} due to error : ${error.message}`);
							continue;
						}
					}

					if (render) {
						try {
							groupData.push(render(lookupData));
						} catch (error) {
							console.log(`Could not render child with index ${index} of group ${group} due to error : ${error.message}`);
						}
					} else {
						let displayValue = "-";

						try {
							displayValue = value(lookupData);
						} catch (error) {
							console.log(`Could not render child with title ${title} of group ${group} due to error : ${error.message}`);
						}

						groupData.push(<ContentComponent key={`${group}-${index}`} title={title} value={displayValue} />);
					}
				}

				renderData.push(
					<DetailsComponent key={group} disableCollapse={disableCollapse} initiallyExpanded={initiallyExpanded} title={title}>
						{groupData}
					</DetailsComponent>
				);
		}
	}

	return <Container>{renderData}</Container>;
}

function transformOrderData(data) {
	if (!data) return;

	for (let item of data.items) {
		let { fullfillmentDetails } = item;

		//TODO: Maybe this could be grouped on the server side, this is not ideal to do.
		if (fullfillmentDetails.tasksData) {
			let tasks = {},
				keys = Object.keys(fullfillmentDetails.tasksData);
			for (const task in TaskTypes) {
				for (const key of keys) {
					if (key.indexOf(TaskTypes[task]) !== -1) {
						const newKey = key.replace(TaskTypes[task], "");
						tasks = setProperty(tasks, fullfillmentDetails.tasksData[key], `${task}.${newKey}`);
					}
				}
			}
			fullfillmentDetails.tasksData = tasks;
		}
	}

	return data;
}

function getConfig({ machineID }) {
	const orderItemConfig = {
		orderDetails: {
			disableCollapse: true,
			title: "Item Details",
			type: DataTypes.Object,
			children: [
				{ title: "Item It", value: (data) => data.itemId },
				{ title: "Recipe", value: (data) => data.itemDetails.recipeId },
				{ title: "Status", value: (data) => data.fullfillmentDetails.status },
				{ title: "Preparation Time", value: (data) => data.itemDetails.preparationTime },
				{ title: "Calories", value: (data) => data.itemDetails.totalCalories },
				{
					title: "Allowed Collection Time",
					value: (data) => <Timestamp value={data.fullfillmentDetails.allowedCollectionStartTime} />,
				},
			],
		},
		ingredientDispenseDetails: {
			initiallyExpanded: true,
			title: "Ingredients",
			retrieveListData: (data) => data?.fullfillmentDetails?.dispenseMatrix,
			type: DataTypes.List,	
			skipIfEmpty: true,
			errored: (data) => data.isDiscrepency,
			children: [
				{ title: "Ingredient", value: (data) => <Pagelink to={`/studio/ingredient/${data.itemId}`}>{data.ingredientName}</Pagelink> },
				{ title: "Expected Amount", value: (data) => data.expectedDispensed },
				{ title: "Actual Dispensed", value: (data) => data.actualDispensed },
				{ title: "Impact Factor", value: (data) => data.ingImpactFactor },
				{ title: "Min Required Amount Percentage", value: (data) => data.minRequiredAmountPercent },
			],
		},
		pricing: {
			initiallyExpanded: true,
			title: "Pricing",
			type: DataTypes.Object,
			children: [
				{ title: "Original Price", value: (data) => `${data.currency}${data.itemDetails.price}` },
				{ title: "Final Price", value: (data) => `${data.currency}${data.itemDetails.finalPrice}` },
				{ title: "Tax Amount", value: (data) => `${data.currency}${data.itemDetails.taxAmount}` },
			],
		},
		coupons: {
			initiallyExpanded: false,
			title: "Coupons",
			type: DataTypes.List,
			retrieveListData: (data) => data?.itemDetails?.offers?.coupons,
			skipIfEmpty: true,
			children: [
				{ title: "Code", value: (data, _) => data.offerCode },
				{ title: "Category", value: (data, _) => data.offerCategory },
				{ title: "Savings", value: (data, _) => `${data.currency}${data.saving}` },
			],
		},
		discounts: {
			initiallyExpanded: false,
			title: "Discounts",
			type: DataTypes.List,
			retrieveListData: (data) => data?.itemDetails?.offers?.discounts,
			skipIfEmpty: true,
			children: [
				{ title: "Code", value: (data, _) => data.offerCode },
				{ title: "Category", value: (data, _) => data.offerCategory },
				{ title: "Saving Per Item", value: (data, originalData) => `${originalData.currency}${data.savingPerItem}` },
			],
		},
		tasks: {
			initiallyExpanded: false,
			title: "Tasks",
			type: DataTypes.List,
			retrieveListData: (data) => data?.fullfillmentDetails?.tasksData,
			skipIfEmpty: true,
			children: [
				{ title: "Task Type", value: (data, _, index, dataIndex) => dataIndex },
				{
					title: "Task Id",
					value: (data) => <Pagelink to={`/${machineID}/task/${data.TaskId}`}>{data.TaskId}</Pagelink>,
					omit: (data) => typeof data.TaskId === "undefined",
				},
				{ title: "Start Time", value: (data) => <Timestamp value={data.StartTime} /> },
				{ title: "End Time", value: (data) => <Timestamp value={data.EndTime} /> },
				{ title: "Duration", value: (data) => fancyTimeFormat(data.Duration) },
			],
		},
	};

	const orderConfig = {
		orderDetails: {
			disableCollapse: true,
			title: "Order Details",
			type: DataTypes.Object,
			children: [
				{ title: "Current Status", value: (data) => data.status },
				{ title: "User Id", value: (data) => data.userId },
				{ title: "Kitchen", value: (data) => data.kitchenId },
				{ title: "Created At", value: (data) => <Timestamp value={data.createdAt} /> },
				{ title: "Updated At", value: (data) => <Timestamp value={data.updatedAt} /> },
				{ title: "Timezone", value: (data) => data.timeZone },
				{
					omit: (data) => typeof data.blockedPayload === "undefined",
					render: (data) => (
						<ModalButton
							title="Blocked Payload"
							buttonText="Blocked Payload"
							render={() => <pre>{JSON.stringify(data.blockedPayload, null, 4)}</pre>}
						/>
					),
				},
				{
					omit: (data) => typeof data.linkVerificationDetails === "undefined",
					render: (data) => (
						<ModalButton
							title="Pagelink Verification Details"
							buttonText="Pagelink Verification Details"
							render={() => <pre>{JSON.stringify(data.linkVerificationDetails, null, 4)}</pre>}
						/>
					),
				},
			],
		},
		paymentDetails: {
			initiallyExpanded: true,
			title: "Payment Details",
			type: DataTypes.Object,
			children: [
				{ title: "Status", value: (data) => data.paymentDetails.paymentStatus },
				{ title: "Payment ID", value: (data) => data.paymentDetails.paymentOrderId },
				{ title: "Amount Paid", value: (data) => `${data.currencySymbol}${data.paymentDetails.amountPaid}` },
				{ title: "Amount Refunded", value: (data) => `${data.currencySymbol}${data.paymentDetails.amountRefunded}`, omit: (data) => typeof data.paymentDetails.amountRefunded === "undefined" },
				{ title: "TOA Amount", value: (data) => `${data.currencySymbol}${data.paymentDetails.toaAmount}`, omit: (data) => typeof data.paymentDetails.toaAmount === "undefined" },
			],
		},
		pricing: {
			initiallyExpanded: true,
			title: "Pricing",
			type: DataTypes.Object,
			children: [
				{ title: "Cart Price", value: (data) => `${data.currencySymbol}${data.cartPrice}` },
				{ title: "Final Price", value: (data) => `${data.currencySymbol}${data.price}` },
				{ title: "Tax Amount", value: (data) => `${data.currencySymbol}${data.taxEntity.taxAmount}` },
			],
		},
		orderItems: {
			initiallyExpanded: true,
			title: "Order Items",
			type: DataTypes.List,
			retrieveListData: (data) => data.items,
			skipIfEmpty: true,
			children: [
				{ title: "Dish", value: (data, _) => data.itemDetails.title },
				{ title: "ItemId", value: (data, _) => data.itemId },
				{ title: "Status", value: (data, _) => data.fullfillmentDetails.status },
				{
					title: "Recipe",
					value: (data, _) => <Pagelink to={`/studio/recipe/${data.itemDetails.recipeId}`}>{data.itemDetails.recipeName || data.itemDetails.recipeId}</Pagelink>,
				},
				{ title: "Preparation Time", value: (data, _) => data.itemDetails.preparationTime },
				{
					render: (data, originalData) => (
						<div style={{ marginLeft: "auto", padding: "0 1rem" }}>
							<ModalButton
								buttonText="View"
								title={data.itemDetails.title}
								render={() => parseConfig(orderItemConfig, { ...data, currency: originalData.currencySymbol })}
							/>
						</div>
					),
				},
			],
		},
		brandDetails: {
			initiallyExpanded: false,
			title: "Brands",
			type: DataTypes.List,
			retrieveListData: (data) => data?.brandDetails,
			children: [
				{ title: "Name", value: (data, _) => data.brandName },
				{ title: "Identifier", value: (data, _) => data.id },
				{
					title: "Brand logo",
					omit: (data) => typeof data?.brandCms?.brandLogo === "undefined",
					value: (data, _) => (
						<a style={{ textDecoration: "underline" }} href={data?.brandCms?.brandLogo} target="_blank" rel="noopener noreferrer">
							View
						</a>
					),
				},
			],
		},
		coupons: {
			initiallyExpanded: false,
			title: "Coupons",
			type: DataTypes.List,
			retrieveListData: (data) => data?.offers?.discounts,
			skipIfEmpty: true,
			children: [
				{ title: "Code", value: (data, _) => data.offerCode },
				{ title: "Category", value: (data, _) => data.offerCategory },
				{ title: "Savings", value: (data, originalData) => `${originalData.currencySymbol}${data.saving}` },
			],
		},
		discounts: {
			initiallyExpanded: false,
			title: "Discounts",
			type: DataTypes.List,
			retrieveListData: (data) => data?.offers?.discounts,
			skipIfEmpty: true,
			children: [
				{ title: "Code", value: (data, _) => data.offerCode },
				{ title: "Category", value: (data, _) => data.offerCategory },
				{ title: "Savings", value: (data, originalData) => `${originalData.currencySymbol}${data.saving}` },
			],
		},
	};

	return orderConfig;
}

function renderHeader(data, navigate, abortOrder, handleBack) {
	if (isEmpty(data)) return;

	return (
		<Div margin="0 0 1rem 0" jContent="space-between" alignItem="center" width="100%" flexWrap="wrap" gap="1rem">
			<Div alignItem="center" gap="1rem">
				<span
					onClick={handleBack}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<Header>{data.orderId}</Header>
			</Div>
			<Div alignItem="center" gap="1rem" >
				<Button onClick={() => navigate(`/${data.machineID}/task/search?taskGroupId=${data.orderId}`)}>All Tasks</Button>
				{data?.orderData?.canBeAborted ? (
					<Button disabled={data.abortCallLoading} onClick={() => abortOrder()}>
						Force Abort Order
					</Button>
				) : (
					""
				)}
			</Div>
		</Div>
	);
}

const OrderDetails = (props) => {
	const { machineID, kitchenId } = props;
	const { id: orderId } = useParams();
	const orderConfig = getConfig({ machineID });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [orderData, setOrderData] = useState(null);
	const [abortCallLoading, setAbortCallLoading] = useState(false);
	const [actionTaken, setActionTaken] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	function handleBack() {
		if (actionTaken) {
			dispatch(clearState());
		}
		if (location.state?.fromList) {
			navigate(-1);
		} else {
			navigate(`/${machineID}/order/recent`);
		}
	}

	async function getOrderDetails(orderId, clearError = false) {
		if (clearError) {
			setError(false);
		}

		setLoading(true);
		const response = await OrderService.getOrderDetails(orderId);
		const { data, error } = response;
		if (data) {
			try {
				const order = transformOrderData(data);
				setOrderData(order);
			} catch (error) {
				console.log(`Could not transform order data due to error : ${error.message}`);
				setError(true);
			} finally {
				setLoading(false);
			}
		}
		if (error) {
			errorNotification(error);
			setLoading(false);
			setError(true);
		}
	}

	async function abortOrder() {
		setAbortCallLoading(true);
		const response = await OrderService.abortOrder(orderId, { kitchenId, abortForcefully: true });
		const { data, error } = response;
		if (data) {
			getOrderDetails(orderId);
			setActionTaken(true);
		}
		if (error) {
			errorNotification(error);
			setAbortCallLoading(false);
		}
	}

	useEffect(() => {
		getOrderDetails(orderId);
	}, []);

	return (
		<Page>
			{renderHeader({ orderId, machineID, orderData, abortCallLoading }, navigate, abortOrder, handleBack)}
			{!loading && !error ? parseConfig(orderConfig, orderData) : ""}
			{loading ? <Loader /> : null}
			{error ? (
				<EmptyState
					message="Something went wrong in fetching order details."
					retry={() => getOrderDetails(machineID, orderId, true)}
					retryButtonText="Try Again"
				/>
			) : null}
		</Page>
	);
};

export default LandingConnector(OrderDetails);
